/**
 * 씬 단위 활성 레이아웃 공유 — safe-area/상단 배너 예약 반영 좌표(deriveShellLayout 결과)를 registry로 흘려
 * 게임뷰/발사체 물리/연출/토스트가 동일 좌표계를 쓰게 한다(시각=물리 정합).
 *
 * GameScene.create가 디바이스 inset을 읽어 {@link setActiveLayout}으로 1회 등록하고,
 * 게임뷰 좌표가 필요한 시스템은 {@link sceneGameviewRect}/{@link sceneLauncherY}로 조회한다.
 * 미등록(테스트·부팅 직전)이면 design 기준(insets=0)으로 폴백해 기존 동작과 동일.
 */
import type Phaser from "phaser";
import { ZERO_INSETS, type SafeAreaInsets } from "@yjsgames/safe-area";
import { deriveShellLayout, type ShellLayout } from "../data/balance.js";
import {
  computeGameviewRect,
  computePlayfieldBounds,
  type GameviewRect,
} from "./gameviewLayout.js";

const ACTIVE_LAYOUT_KEY = "activeLayout";

const ACTIVE_INSETS_KEY = "activeInsets";

/**
 * 전투탭 펼치기 시 게임뷰 콘텐츠 상향 오프셋(px). 발사대·벽돌·발사체·playfieldBounds를 **함께** 위로 옮겨
 * (발사대-벽돌 상대 거리 = 난이도 보존), 넘치는 상단은 마스크가 클립한다. ShellFrame.setGameviewFraction 반환 N.
 */
const GAMEVIEW_OFFSET_KEY = "gameviewContentOffsetY";
const GAMEVIEW_VISIBLE_FRACTION_KEY = "gameviewVisibleFraction";

/**
 * 씬의 활성 레이아웃과 world inset을 등록한다.
 *
 * GameScene.create 시점에 디바이스 safe-area inset과 native 상단 배너 예약을 읽어 1회 호출한다.
 * 이후 {@link getActiveLayout}·{@link getActiveInsets}·{@link sceneGameviewRect} 등
 * 조회 함수들은 여기서 등록된 값을 반환하며, 게임뷰/물리/연출이 동일 좌표계를 공유하게 된다.
 *
 * @param scene Phaser.Scene 인스턴스 — registry가 저장소로 사용된다.
 * @param layout deriveShellLayout으로 산출된 shell 레이아웃.
 * @param insets world 단위로 환산된 콘텐츠 inset. top에는 native 상단 배너 예약이 포함될 수 있다.
 */
export function setActiveLayout(
  scene: Phaser.Scene,
  layout: ShellLayout,
  insets: SafeAreaInsets,
): void {
  scene.registry.set(ACTIVE_LAYOUT_KEY, layout);
  scene.registry.set(ACTIVE_INSETS_KEY, insets);
}

/** 활성 콘텐츠 inset(world) — 벽돌 그리드 등 design 좌표를 게임뷰와 동일하게 압축할 때 사용. */
export function getActiveInsets(scene: Phaser.Scene): SafeAreaInsets {
  return (scene.registry.get(ACTIVE_INSETS_KEY) as SafeAreaInsets | undefined) ?? ZERO_INSETS;
}

/** 등록된 활성 레이아웃 — 미등록 시 design 기준(insets=0) 폴백. */
export function getActiveLayout(scene: Phaser.Scene): ShellLayout {
  return (
    (scene.registry.get(ACTIVE_LAYOUT_KEY) as ShellLayout | undefined) ??
    deriveShellLayout(ZERO_INSETS)
  );
}

/** 활성 레이아웃 기준 게임뷰 Rect (safe-area 압축 반영). */
export function sceneGameviewRect(scene: Phaser.Scene): GameviewRect {
  return computeGameviewRect(scene.scale.width, getActiveLayout(scene));
}

/** 현재 화면에 실제로 보이는 게임뷰 Rect. 펼치기 상태에서는 탭 패널을 침범하지 않도록 클립 높이를 반영한다. */
export function sceneVisibleGameviewRect(scene: Phaser.Scene): GameviewRect {
  const gv = sceneGameviewRect(scene);
  const fraction = getGameviewVisibleFraction(scene);
  if (fraction >= 1) return gv;
  return {
    ...gv,
    outerH: gv.outerH * fraction,
    intH: gv.intH * fraction,
  };
}

/** 활성 레이아웃 기준 발사체 벽 반사 경계. 펼치기 콘텐츠 오프셋만큼 위로 평행이동(높이 불변 — 난이도 보존). */
export function scenePlayfieldBounds(scene: Phaser.Scene): {
  left: number;
  right: number;
  top: number;
  bottom: number;
} {
  const b = computePlayfieldBounds(sceneGameviewRect(scene));
  const offset = getGameviewContentOffset(scene);
  return { left: b.left, right: b.right, top: b.top - offset, bottom: b.bottom - offset };
}

/**
 * 전투탭 펼치기 게임뷰 콘텐츠 상향 오프셋 설정 — ShellFrame.setGameviewFraction 반환값(N)을 GameScene이 등록한다.
 * sceneLauncherY·scenePlayfieldBounds가 이 값을 빼고, BattlefieldRunner가 벽돌 배치/이동에 적용한다.
 */
export function setGameviewContentOffset(scene: Phaser.Scene, offsetY: number): void {
  scene.registry.set(GAMEVIEW_OFFSET_KEY, offsetY);
}

/** 현재 게임뷰 콘텐츠 상향 오프셋(px) — 미설정 0. 벽돌 신규 배치(layoutBrickCells)·기존 이동에 사용. */
export function getGameviewContentOffset(scene: Phaser.Scene): number {
  return (scene.registry.get(GAMEVIEW_OFFSET_KEY) as number | undefined) ?? 0;
}

/** 게임뷰 시각 클립 비율. 접기=1, 펼치기=GAMEVIEW_EXPAND_FRACTION. */
export function setGameviewVisibleFraction(scene: Phaser.Scene, fraction: number): void {
  scene.registry.set(GAMEVIEW_VISIBLE_FRACTION_KEY, clampGameviewFraction(fraction));
}

export function getGameviewVisibleFraction(scene: Phaser.Scene): number {
  const value = scene.registry.get(GAMEVIEW_VISIBLE_FRACTION_KEY);
  return typeof value === "number" ? clampGameviewFraction(value) : 1;
}

function clampGameviewFraction(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(1, Math.max(0, value));
}

/** 활성 레이아웃 기준 발사 원점 y (발사대 그림과 정합). 펼치기 콘텐츠 오프셋만큼 위로. */
export function sceneLauncherY(scene: Phaser.Scene): number {
  return getActiveLayout(scene).launcherY - getGameviewContentOffset(scene);
}
